"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useLocale, useTranslations } from "next-intl";
import {
  Bot,
  Brain,
  Check,
  Copy,
  PanelLeft,
  MessageSquarePlus,
  History,
  ImageIcon,
  Paperclip,
  PenLine,
  RefreshCw,
  Sparkles,
  Wrench,
  X,
  Globe,
  Coins,
  ChevronDown,
  Ellipsis,
  CircleAlert,
  ExternalLink,
  Lock,
  UsersRound,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { animate, motion, useMotionValue, useTransform } from "motion/react";
import { useChat } from "@ai-sdk/react";
import type { LevelAccessHint } from "@/lib/user-level/access";
import { DefaultChatTransport } from "ai";

import {
  AiPromptPicker,
  type PickedPrompt,
} from "@/components/ai/ai-prompt-picker";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { SelectGroup, SelectLabel } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ImageSkeleton } from "@/components/ui/image-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  buildPromptScaffold,
  composePromptMessage,
} from "@/lib/ai/prompt/vocab";
import {
  modelAttachmentKinds,
  modelAcceptsAttachment,
} from "@/lib/ai/model-features";
import { attachmentLimitIssue, remainingAttachmentCount, type AiInputLimits } from "@/lib/ai/input-limits";
import {
  buildTurnSteps,
  chatMessageSegments,
  chatMessageText,
  chatToolNote,
  isOwnChatError,
  mergeToolEvent,
  MAX_CHAT_CONTENT_CHARS,
  readChatErrorMessage,
  toUIMessage,
  type ChatToolEvent,
  type ChatUIMessage,
  type ConversationRunStatus,
} from "@/lib/ai/chat-ui";
import { ChatToolCard } from "@/components/ai/chat-tool-card";
import { ChatArtifacts } from "@/components/ai/chat-artifacts";
import { SentAttachments } from "@/components/ai/chat-sent-attachments";
import { AiConversationManager } from "@/components/ai/ai-conversation-manager";
import { AgentPurchaseDialog } from "@/components/ai/agent-purchase-dialog";
import {
  ConversationActionDialogs,
  ConversationMenu,
  ConversationTitleTip,
  type ConversationAction,
} from "@/components/ai/ai-conversation-actions";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Loader } from "@/components/ai-elements/loader";
import {
  Attachment,
  AttachmentInfo,
  AttachmentPreview,
  AttachmentRemove,
  Attachments,
} from "@/components/ai-elements/attachments";
import { uploadChatFile, uploadChatDroppedFiles } from "@/lib/ai/chat-attachment-upload";
import { AttachPickerModal } from "@/components/admin/attach-picker/attach-picker-modal";
import type { AttachFileRecord } from "@/components/admin/attach-picker/types";
import { Source } from "@/components/ai-elements/sources";
import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import {
  Queue,
  QueueItem,
  QueueItemAction,
  QueueItemActions,
  QueueItemContent,
  QueueItemIndicator,
  QueueList,
  QueueSection,
  QueueSectionContent,
  QueueSectionLabel,
  QueueSectionTrigger,
} from "@/components/ai-elements/queue";
import {
  Context,
  ContextContent,
  ContextContentBody,
  ContextContentFooter,
  ContextTrigger,
} from "@/components/ai-elements/context";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatDateTime } from "@/lib/datetime/display";
import { Image } from "@/components/ai-elements/image";
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
} from "@/components/ai-elements/chain-of-thought";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
  MessageToolbar,
} from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputProvider,
  PromptInputSelect,
  PromptInputSelectContent,
  PromptInputSelectItem,
  PromptInputSelectTrigger,
  PromptInputSelectValue,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  PromptInputHeader,
  usePromptInputAttachments,
  usePromptInputController,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ImageGallery } from "@/components/ui/image-gallery";
import {
  StreamingMarkdown,
  type CitedSource,
} from "@/components/ui/streaming-markdown";
import { CopyButton } from "@/components/ui/copy-button";
import { cn } from "@/lib/utils";
import {
  modelAllowsThinking,
  modelAllowsWebSearch,
} from "@/lib/ai/chat-switches";
import { useChatErrorText } from "@/components/ai/use-chat-error-text";
import { apiFetch } from "@/lib/api-client";
import { ContentSkeleton } from "@/components/site/content-skeleton";
import { refreshAuthSession } from "@/lib/auth/refresh-client";

const CONVERSATION_PARAM = "conversationId";

function writeConversationParam(id: string | null) {
  const url = new URL(window.location.href);

  if (id) {
    url.searchParams.set(CONVERSATION_PARAM, id);
  } else {
    url.searchParams.delete(CONVERSATION_PARAM);
  }

  window.history.replaceState(null, "", url);
}

const MEMBER_CHAT_TRANSPORT = new DefaultChatTransport<ChatUIMessage>({
  api: "/api/member/ai/chat",
  prepareSendMessagesRequest: ({ body }) => ({ body: body ?? {} }),

  fetch: async (input, init) => {
    const response = await fetch(input, init);

    if (response.status !== 401) return response;

    const refreshed = await refreshAuthSession("member");

    return refreshed.ok ? fetch(input, init) : response;
  },
});

type ChatModel = {
  id: string;
  modelId: string;
  displayName: string;
  providerName: string;
  scope: string;
  inputModalities: string[];
  inputLimits?: AiInputLimits;
  capabilities: string[];
  contextWindow: number;

  kind: string;
};

type ConversationSummary = {
  id: string;
  title: string;
  costPoints: number;
  updatedAt: string;
  shared: boolean;
  runStatus: ConversationRunStatus;

  agent: { id: string; name: string } | null;
};

type ChatMessageRow = {
  id: string;
  role: string;
  content: string;

  reasoning: string;

  toolTrace: ChatToolEvent[] | null;

  files: { mediaType: string; url: string }[] | null;

  sources: { url: string; title: string }[] | null;

  modelId: string;
  modelLabel: string;
  inputTokens: number;
  cachedTokens: number;
  cacheWriteTokens?: number;
  outputTokens: number;
  costPoints: number;
  aborted: boolean;
  createdAt: string;

  error: string;
};

type PageData = {
  conversations: ConversationSummary[];
  models: ChatModel[];
  points: number;
  agentAccess?: LevelAccessHint & { agentId: string } | null;
};

const RAIL_CONVERSATION_LIMIT = 15;

const HISTORY_MANAGER_THRESHOLD = 5;

const RUN_POLL_MS = 3000;

function markConversationRead(id: string) {
  return apiFetch(`/api/member/ai/conversations/${id}/read`, { method: "POST" }).catch(
    () => undefined,
  );
}

function TypedTitle({ text, onDone }: { text: string; onDone: () => void }) {
  const count = useMotionValue(0);
  const shown = useTransform(count, (value) => text.slice(0, Math.round(value)));

  useEffect(() => {
    const controls = animate(count, text.length, {
      duration: text.length * 0.06,
      ease: "linear",
      onComplete: onDone,
    });

    return () => controls.stop();
  }, [count, text, onDone]);

  return <motion.span>{shown}</motion.span>;
}

function RunDot({ status }: { status: ConversationRunStatus }) {
  return (
    <span
      aria-hidden
      className={cn(
        "size-2 shrink-0 rounded-full",
        status === "running"
          ? "animate-pulse bg-brand"
          : status === "success"
            ? "bg-emerald-500"
            : status === "failed"
              ? "bg-destructive"
              : "border border-muted-foreground/50",
      )}
    />
  );
}


type SentAttachment = {
  attachId: string;
  mediaType: string;
  url: string;
  filename: string;
};

function sentAttachmentsOf(message: ChatUIMessage): SentAttachment[] {
  return message.parts.flatMap((part) => {
    const attachId = part.type === "file" ? part.providerMetadata?.attach?.id : undefined;

    return part.type === "file" && typeof attachId === "string"
      ? [
          {
            attachId,
            mediaType: part.mediaType,
            url: part.url,
            filename: part.filename ?? "",
          },
        ]
      : [];
  });
}

const SCOPE_KEYS = ["platform", "enterprise", "personal"] as const;

export type AiChatPanelProps = {

  agent?: {
    id: string;
    name: string;

    presetQuestions?: string[];
    defaultModelId?: string | null;
    requiresTools?: boolean;
  } | null;

  sideExtra?: ReactNode;

  contextPane?: ReactNode;

  loggedIn?: boolean;
  loginHref?: string;

  className?: string;

  railTargetId?: string;

  historyAgentId?: string;

  expertIdentity?: { name: string; avatarUrl?: string | null };

  showPromptPicker?: boolean;

  purchase?: { price: number } | null;
  membershipAccess?: LevelAccessHint & { agentId: string } | null;
};

const noopSubscribe = () => () => {};

export function AiChatPanel(props: AiChatPanelProps) {
  return (
    <PromptInputProvider>
      <AiChatPanelInner {...props} />
    </PromptInputProvider>
  );
}

function AiChatPanelInner({
  agent,
  sideExtra,
  contextPane,
  loggedIn = true,
  loginHref = "/member/login/",
  className,
  railTargetId,
  historyAgentId,
  expertIdentity,
  showPromptPicker = true,
  purchase = null,
  membershipAccess = null,
}: AiChatPanelProps) {
  const t = useTranslations("member.aiChat");
  const attachText = useTranslations("site.attach");
  const inputText = useTranslations("site.server.ai.attach");
  const ui = useTranslations("site.ui");
  const chatErrorText = useChatErrorText();
  const scopeLabel = (scope: string) =>
    (SCOPE_KEYS as readonly string[]).includes(scope)
      ? t(`scope.${scope as (typeof SCOPE_KEYS)[number]}`)
      : scope;
  const { textInput, attachments } = usePromptInputController();
  const rootRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<PageData | null>(null);
  const [pageLoading, setPageLoading] = useState(loggedIn);
  const accessAgentId = agent?.id ?? historyAgentId;
  const currentAccess = accessAgentId && data?.agentAccess?.agentId === accessAgentId ? data.agentAccess : accessAgentId && membershipAccess?.agentId === accessAgentId ? membershipAccess : null;
  const membershipBlocked = currentAccess?.allowed === false || currentAccess?.exclusive === true && currentAccess.usable === false;
  const accessPending = Boolean(loggedIn && accessAgentId && !currentAccess);
  const membershipMessage = currentAccess?.requiredLevelName ? t("membershipRequired", { level: currentAccess.requiredLevelName }) : t("accessRequired");
  const [pageError, setPageError] = useState<string | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const pageRequest = useRef<AbortController | null>(null);
  const conversationRequest = useRef<AbortController | null>(null);
  const displayedConversation = useRef<string | null>(null);
  useEffect(() => () => { pageRequest.current?.abort(); conversationRequest.current?.abort(); }, []);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [modelId, setModelId] = useState("");
  const [thinking, setThinking] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const agentId = agent?.id ?? "";
  const models = (data?.models ?? []).filter(
    (item) => !agentId || (item.kind === "chat" && (!agent?.requiresTools || item.capabilities.includes("tools"))),
  );
  const activeModel = models.find((item) => item.id === modelId);
  const imageMode = activeModel?.kind === "image_gen";
  const thinkingAllowed =
    !imageMode && !!activeModel && modelAllowsThinking(activeModel.capabilities);
  const webSearchAllowed =
    !imageMode && !!activeModel && modelAllowsWebSearch(activeModel.capabilities);

  const [prompt, setPrompt] = useState<PickedPrompt | null>(null);

  const [pendingTools, setPendingTools] = useState<ChatToolEvent[]>([]);
  const [toolEvents, setToolEvents] = useState<Record<string, ChatToolEvent[]>>(
    {},
  );

  const [messageModel, setMessageModel] = useState<Record<string, string>>({});

  const [railOpen, setRailOpen] = useState(false);

  const railTarget = useSyncExternalStore(
    noopSubscribe,
    () => (railTargetId ? document.getElementById(railTargetId) : null),
    () => null,
  );

  const [managerOpen, setManagerOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const router = useRouter();

  const [conversationAction, setConversationAction] = useState<ConversationAction | null>(
    null,
  );

  const awaitingRun = useRef<{ id: string; sawRunning: boolean } | null>(null);

  const [titleRefresh, setTitleRefresh] = useState<{ id: string; initialTitle: string } | null>(null);

  const [typingTitle, setTypingTitle] = useState<string | null>(null);
  const finishTyping = useCallback(() => setTypingTitle(null), []);
  const updateTitle = useCallback((id: string, title: string) => {
    setData((current) => current ? {
      ...current,
      conversations: current.conversations.map((item) => item.id === id ? { ...item, title } : item),
    } : current);
    setTypingTitle(id);
  }, []);

  const { messages, sendMessage, setMessages, stop, status, error, clearError } =
    useChat<ChatUIMessage>({
      transport: MEMBER_CHAT_TRANSPORT,
      onData: (part) => {
        if (part.type === "data-meta") {
          const created = !conversationId;

          setConversationId(part.data.conversationId);
          displayedConversation.current = part.data.conversationId;
          writeConversationParam(part.data.conversationId);

          if (created) {
            void loadPage();
          }
        } else if (part.type === "data-title") {
          const id = part.data.conversationId ?? conversationId;
          if (id && part.data.pending) {
            setTitleRefresh({ id, initialTitle: part.data.title ?? "" });
          } else if (id && part.data.title) {
            updateTitle(id, part.data.title);
          }
        } else if (part.type === "data-tool") {

          setPendingTools((list) => mergeToolEvent(list, part.data));
        }
      },
      onFinish: ({ message, isAbort, isDisconnect }) => {

        setToolEvents((current) =>
          pendingTools.length > 0
            ? { ...current, [message.id]: pendingTools }
            : current,
        );
        setPendingTools([]);

        const watched = conversationId && !isAbort && !isDisconnect ? conversationId : null;

        void (watched ? markConversationRead(watched) : Promise.resolve()).then(loadPage);
      },

      onError: () => {},
    });

  const streaming = status === "submitted" || status === "streaming";

  const [queue, setQueue] = useState<
    { id: string; text: string; files: SentAttachment[] }[]
  >([]);

  const [uploading, setUploading] = useState(false);
  const composerUploadBusy = useRef(false);

  const [sourcePanel, setSourcePanel] = useState<{
    sources: CitedSource[];
    active?: string;
  } | null>(null);

  const [picked, setPicked] = useState<SentAttachment[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const lastStatus = useRef(status);

  const loadPage = useCallback(async () => {

    if (!loggedIn) return;
    pageRequest.current?.abort();
    const controller = new AbortController();
    pageRequest.current = controller;
    setPageLoading(true);
    setPageError(null);

    try {
      const query = historyAgentId || agentId
        ? `?${new URLSearchParams({ agentId: historyAgentId || agentId! })}`
        : "";
      const result = await apiFetch<PageData>(`/api/member/ai/conversations${query}`, { signal: controller.signal });
      if (controller.signal.aborted) return;

      setData(result);
      const available = result.models.filter(item => !agentId || (
        item.kind === "chat" && (!agent?.requiresTools || item.capabilities.includes("tools"))
      ));
      setModelId(current => available.find(item => item.id === current)?.id ||
        available.find(item => item.id === agent?.defaultModelId)?.id || available[0]?.id || "");
    } catch (error) {
      if (controller.signal.aborted) return;
      const message = error instanceof Error ? error.message : t("loadFailed");
      setPageError(message);
      toast.error(message);
    } finally {
      if (pageRequest.current === controller && !controller.signal.aborted) setPageLoading(false);
    }
  }, [t, loggedIn, agentId, agent?.requiresTools, agent?.defaultModelId, historyAgentId]);

  const promptLogin = useCallback(() => {
    toast.info(t("loginRequired"), {
      action: {
        label: t("goLogin"),
        onClick: () => {
          window.location.href = loginHref;
        },
      },
    });
  }, [t, loginHref]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (!titleRefresh) return;
    const until = Date.now() + 30_000;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const result = await apiFetch<{ title: string }>(`/api/member/ai/conversations/${encodeURIComponent(titleRefresh.id)}/title`);
        if (!active) return;
        if (result.title !== titleRefresh.initialTitle || Date.now() >= until) {
          if (result.title !== titleRefresh.initialTitle) {
            updateTitle(titleRefresh.id, result.title);
          }
          setTitleRefresh(null);
          return;
        }
      } catch {
        if (active) setTitleRefresh(null);
        return;
      }
      timer = setTimeout(() => void refresh(), 2000);
    };
    timer = setTimeout(() => void refresh(), 1000);
    return () => { active = false; clearTimeout(timer); };
  }, [titleRefresh, updateTitle]);

  const polling = (data?.conversations ?? []).some(
    (item) => item.runStatus === "running" && !(item.id === conversationId && streaming),
  );

  useEffect(() => {
    if (!polling) return;

    const timer = window.setInterval(() => void loadPage(), RUN_POLL_MS);

    return () => window.clearInterval(timer);
  }, [polling, loadPage]);

  const openConversation = useCallback(async (id: string) => {
    conversationRequest.current?.abort();
    const controller = new AbortController();
    conversationRequest.current = controller;
    setConversationLoading(displayedConversation.current !== id);
    clearError();
    setConversationId(id);
    writeConversationParam(id);

    try {
      const result = await apiFetch<{ messages: ChatMessageRow[] }>(
        `/api/member/ai/conversations/${id}${historyAgentId ? `?${new URLSearchParams({ agentId: historyAgentId })}` : ""}`,
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      displayedConversation.current = id;

      setMessages(
        result.messages.map((row, index) => {
          const asked = result.messages[index - 1];

          return toUIMessage({
            ...row,
            durationMs:
              row.role === "assistant" && asked?.role === "user"
                ? Date.parse(row.createdAt) - Date.parse(asked.createdAt)
                : undefined,
          });
        }),
      );

      setToolEvents(
        Object.fromEntries(
          result.messages
            .filter((row) => row.toolTrace && row.toolTrace.length > 0)
            .map((row) => [row.id, row.toolTrace as ChatToolEvent[]]),
        ),
      );

      setMessageModel(
        Object.fromEntries(
          result.messages
            .filter((row) => row.role === "assistant" && row.modelId)
            .map((row) => [row.id, row.modelId]),
        ),
      );
    } catch (error) {
      if (controller.signal.aborted) return;
      displayedConversation.current = null;

      setConversationId(null);
      setMessages([]);
      setToolEvents({});
      setMessageModel({});
      awaitingRun.current = null;
      writeConversationParam(null);
      toast.error(error instanceof Error ? error.message : t("loadConversationFailed"));
    } finally {
      if (conversationRequest.current === controller && !controller.signal.aborted) setConversationLoading(false);
    }
  }, [setMessages, t, clearError, historyAgentId]);

  useEffect(() => {
    if (!loggedIn) return;

    const id = new URLSearchParams(window.location.search).get(
      CONVERSATION_PARAM,
    );

    if (!id) return;

    let alive = true;
    awaitingRun.current = { id, sawRunning: false };
    void Promise.resolve().then(() => {
      if (alive) void openConversation(id);
    });

    return () => {
      alive = false;
    };
  }, [loggedIn, openConversation]);

  useEffect(() => {
    const awaiting = awaitingRun.current;

    if (!awaiting || awaiting.id !== conversationId) return;

    const item = data?.conversations.find((row) => row.id === awaiting.id);

    if (!item) return;

    if (item.runStatus === "running") {
      awaiting.sawRunning = true;
      return;
    }

    awaitingRun.current = null;

    if (awaiting.sawRunning) {
      void Promise.resolve().then(() => openConversation(awaiting.id));
    }
  }, [data, conversationId, openConversation]);

  function leaveStreaming() {
    if (!streaming) return;

    setQueue([]);
    void stop();
  }

  function switchConversation(id: string) {
    if (id === conversationId) return;

    leaveStreaming();
    awaitingRun.current =
      data?.conversations.find((item) => item.id === id)?.runStatus === "running"
        ? { id, sawRunning: true }
        : null;
    void openConversation(id);
  }

  function startNewChat() {
    conversationRequest.current?.abort();
    displayedConversation.current = null;
    setConversationLoading(false);
    leaveStreaming();
    awaitingRun.current = null;
    clearError();
    setConversationId(null);
    writeConversationParam(null);
    setMessages([]);
    textInput.clear();
    setPrompt(null);
  }

  function submit(text: string, files: SentAttachment[] = []) {
    if (membershipBlocked || accessPending) { if (membershipBlocked) toast.error(membershipMessage); return; }
    if (!text || conversationLoading || (pageLoading && !data)) return;

    if (streaming) {
      setQueue((list) => [...list, { id: crypto.randomUUID(), text, files }]);
      return;
    }

    if (!loggedIn) {
      promptLogin();
      return;
    }

    if (!modelId) {
      toast.error(t("selectModelFirst"));
      return;
    }

    setPrompt(null);
    setPendingTools([]);

    void sendMessage(
      {
        text,
        files: files.map((file) => ({
          type: "file" as const,
          mediaType: file.mediaType,
          url: file.url,
          filename: file.filename,
          providerMetadata: { attach: { id: file.attachId } },
        })),
      },
      {
        body: {
          conversationId,
          modelId,
          content: text,
          ...(files.length > 0
            ? { attachIds: files.map((file) => file.attachId) }
            : {}),

          ...(agent && !conversationId ? { agentId: agent.id } : {}),
          ...(thinkingAllowed ? { thinking } : {}),
          ...(webSearchAllowed ? { webSearch } : {}),
        },
      },
    );
  }

  useEffect(() => {
    const was = lastStatus.current;
    lastStatus.current = status;

    if (status !== "ready" || (was !== "streaming" && was !== "submitted")) {
      return;
    }

    const next = queue[0];

    if (!next) return;

    queueMicrotask(() => {
      setQueue((list) => list.slice(1));
      submit(next.text, next.files);
    });
  });

  async function handleFilesDrop(files: File[]) {
    if (!files.length) return;
    if (membershipBlocked || accessPending) { if (membershipBlocked) toast.error(membershipMessage); return; }
    if (!loggedIn) { promptLogin(); return; }
    if (composerUploadBusy.current) { toast.error(attachText("uploadInProgress")); return; }
    if (!modelProfile) { toast.error(t("attachmentNotSupported")); return; }
    composerUploadBusy.current = true;
    setUploading(true);
    try {
      await uploadChatDroppedFiles({
        files,
        profile: modelProfile,
        existingFiles: [...picked, ...attachments.files],
        messages: {
          failed: t("uploadFailed"), network: t("uploadNetworkError"),
          unsupported: t("attachmentNotSupported"),
          countLimit: issue => inputText("countLimit", issue),
          invalidFile: (name, reason, rules) => attachText("fileError", { name, message:
            reason === "empty" ? attachText("fileRequired")
              : reason === "tooLarge" ? attachText("fileTooLarge", { max: rules.maxSizeMb })
                : attachText("onlySupports", { extensions: rules.extensions.join(attachText("listSeparator")) }),
          }),
        },
        onUploaded: file => setPicked(current => [...current, file]),
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("uploadFailed"));
    } finally {
      composerUploadBusy.current = false;
      setUploading(false);
    }
  }

  async function handleSubmit(message: PromptInputMessage) {
    if (membershipBlocked || accessPending) { if (membershipBlocked) toast.error(membershipMessage); return; }
    if (composerUploadBusy.current) throw new Error(attachText("uploadInProgress"));
    const typed = message.text ?? "";
    const content = prompt
      ? composePromptMessage(prompt.body, prompt.variables, typed)
      : typed.trim();

    if (!content) return;

    if (picked.length + message.files.length > 0 && (!modelProfile || [...picked, ...message.files].some(file => !modelAcceptsAttachment(modelProfile, file.mediaType, file.filename)))) {
      toast.error(t("attachmentNotSupported"));
      throw new Error("unsupported model attachment");
    }

    const limitIssue = attachmentLimitIssue(modelProfile?.inputLimits, [...picked, ...message.files]);
    if (limitIssue) {
      const error = inputText("countLimit", limitIssue);
      toast.error(error);
      throw new Error(error);
    }

    if (content.length > MAX_CHAT_CONTENT_CHARS) {
      toast.error(t("tooLong", { max: MAX_CHAT_CONTENT_CHARS }));
      throw new Error("content too long");
    }

    let dropped: SentAttachment[] = [];

    if (message.files.length > 0) {
      if (!loggedIn) {
        promptLogin();
        throw new Error("login required");
      }

      setUploading(true);
      composerUploadBusy.current = true;

      try {
        const messages = { failed: t("uploadFailed"), network: t("uploadNetworkError") };

        dropped = await Promise.all(
          message.files.map((file) => uploadChatFile(file, messages)),
        );
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t("uploadFailed"));
        throw error;
      } finally {
        composerUploadBusy.current = false;
        setUploading(false);
      }
    }

    textInput.clear();
    setPicked([]);
    submit(content, [...picked, ...dropped]);
  }

  function addPicked(records: AttachFileRecord[]) {
    const next = [...picked];

    for (const record of records) {
      if (!modelProfile || !modelAcceptsAttachment(modelProfile, record.mimeType ?? "", record.originalName || record.fileName)) {
        toast.error(t("attachmentNotSupported"));
        continue;
      }
      if (next.some((item) => item.attachId === record.id)) continue;

      next.push({
        attachId: record.id,
        mediaType: record.mimeType ?? "application/octet-stream",
        url: record.url,
        filename: record.originalName || record.fileName,
      });
    }

    const issue = attachmentLimitIssue(modelProfile?.inputLimits, [...next, ...attachments.files]);
    if (issue) {
      toast.error(inputText("countLimit", issue));
      return;
    }

    setPicked(next);
  }

  function pickModel(id: string) {
    setModelId(id);
    setThinking(false);
    setWebSearch(false);
  }

  function fillInput(text: string) {
    textInput.setInput(text);
    window.requestAnimationFrame(() => {
      const box = rootRef.current?.querySelector("textarea");

      box?.focus();
      box?.setSelectionRange(text.length, text.length);
    });
  }

  function editQuestion(message: ChatUIMessage) {
    fillInput(chatMessageText(message));
    setPicked(sentAttachmentsOf(message));
  }

  function resendQuestion(index: number, dropUnsaved: boolean) {
    for (let i = index - 1; i >= 0; i -= 1) {
      const question = messages[i];

      if (question.role !== "user") continue;

      if (dropUnsaved) setMessages(messages.slice(0, i));

      submit(chatMessageText(question), sentAttachmentsOf(question));
      return;
    }
  }

  const hasImageModel = models.some((item) => item.kind === "image_gen");

  const modelOptions = models.map((item) => ({
    value: item.id,
    label: item.displayName,
  }));

  const modelGroups = models.reduce<
    { key: string; label: string; items: typeof models }[]
  >((groups, item) => {
    const key = `${item.scope}:${item.providerName}`;
    const found = groups.find((group) => group.key === key);
    if (found) {
      found.items.push(item);
    } else {
      groups.push({
        key,
        label:
          item.scope === "platform"
            ? item.providerName
            : t("providerWithScope", { name: item.providerName, scope: scopeLabel(item.scope) }),
        items: [item],
      });
    }
    return groups;
  }, []);
  const modelProfile = activeModel
    ? {
        kind: activeModel.kind,
        inputModalities: activeModel.inputModalities,
        inputLimits: activeModel.inputLimits,
        capabilities: activeModel.capabilities,
      }
    : null;
  const attachmentKinds = modelProfile ? modelAttachmentKinds(modelProfile) : [];
  const attachAllowed = Boolean(activeModel) && attachmentKinds.length > 0;
  const remainingAttachments = remainingAttachmentCount(modelProfile?.inputLimits, picked.length + attachments.files.length);


  const historyMoreButton =
    (data?.conversations.length ?? 0) > HISTORY_MANAGER_THRESHOLD ? (
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label={t("history.manage")}
              className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setManagerOpen(true)}
            />
          }
        >
          <Ellipsis className="size-4" />
        </TooltipTrigger>
        <TooltipContent>{t("history.moreTip")}</TooltipContent>
      </Tooltip>
    ) : null;

  const rail = (
    <>
      {!railTargetId ? (
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={() => {
            startNewChat();
            setRailOpen(false);
          }}
        >
          <MessageSquarePlus className="mr-2 size-4" />
          {t("newChat")}
        </Button>
      ) : null}

      {sideExtra}

      {/* 专家页的入口在对话顶部；会员中心由面板自己画标题。 */}
      {railTargetId ? null : (
        <div className="flex items-center justify-between px-2 pt-1 text-xs text-muted-foreground">
          <span>{t("history.title")}</span>
          {historyMoreButton}
        </div>
      )}

      {/* 专家页显示已加载记录并在框内滚动；会员侧栏保持最近15条。 */}
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain">
        {pageLoading && !data ? <ContentSkeleton variant="rows" count={3} className="px-2" label={ui("loading")} /> : pageError && !data ? <p role="alert" className="px-2 py-4 text-sm text-destructive">{pageError}</p> : null}
        {historyAgentId && !pageLoading && !pageError && !streaming && (!loggedIn || data?.conversations.length === 0) ? (
          <p className="px-2 py-4 text-sm text-muted-foreground">
            {loggedIn ? t("history.empty") : t("loginRequired")}
          </p>
        ) : null}
        {/*
          刚发出第一句：先占一行，直到新会话真的出现在列表里才撤——会话建好与列表拉回来
          之间有一小段空档，提前撤掉列表会先缩一行再撑开，看着像跳了一下
        */}
        {streaming && !data?.conversations.some((item) => item.id === conversationId) ? (
          <div className="flex items-center gap-2 rounded-md bg-muted px-2 py-1.5 text-sm">
            <RunDot status="running" />
            <span className="flex h-5 flex-1 items-center">
              <Skeleton className="h-3.5 w-3/4" />
            </span>
          </div>
        ) : null}
        {(historyAgentId ? data?.conversations ?? [] : (data?.conversations ?? []).slice(0, RAIL_CONVERSATION_LIMIT)).map((item) => (
          <div
            key={item.id}
            className={cn(
              "group relative flex items-center gap-2 rounded-md px-2 py-1.5 text-sm [&:hover]:bg-muted",
              item.id === conversationId && "bg-muted",
            )}
          >
            <RunDot
              status={
                item.id === conversationId
                  ? streaming || item.runStatus === "running"
                    ? "running"
                    : null
                  : item.runStatus
              }
            />
            <ConversationTitleTip
              conversation={item}
              trigger={
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left"
                  onClick={() => {
                    switchConversation(item.id);
                    setRailOpen(false);
                  }}
                >
                  {item.id === typingTitle ? (
                    <TypedTitle text={item.title} onDone={finishTyping} />
                  ) : (
                    item.title || t("newChat")
                  )}
                </button>
              }
            />
            {/* 悬停才浮出来，盖在标题末尾；底色跟着行的悬停色，压住底下的字 */}
            <ConversationMenu
              conversation={item}
              onAction={setConversationAction}
              className="absolute top-1/2 right-1 size-6 -translate-y-1/2 bg-muted opacity-0 focus-visible:opacity-100 data-popup-open:bg-background data-popup-open:opacity-100 [&:hover]:bg-background [.group:hover_&]:opacity-100"
            />
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div
      ref={rootRef}
      className={cn(
        "flex h-[calc(100svh-16rem)] gap-4 md:h-[calc(100vh-12rem)]",
        className,
      )}
    >
      {railTargetId ? (
        railTarget ? (
          createPortal(
            <div className="flex h-full min-h-0 flex-col gap-2">{rail}</div>,
            railTarget,
          )
        ) : null
      ) : (
        <aside className="hidden w-60 shrink-0 flex-col gap-2 border-r pr-4 md:flex">
          {rail}
        </aside>
      )}

      {/* 内置历史栏在窄屏收进抽屉；外部 tab 挂载时无需重复渲染。 */}
      {!railTargetId ? (
        <Sheet open={railOpen} onOpenChange={setRailOpen}>
          <SheetContent side="left" className="w-72 p-4">
            <SheetHeader className="sr-only">
              <SheetTitle>{t("conversations")}</SheetTitle>
            </SheetHeader>
            <div className="flex h-full flex-col gap-2">{rail}</div>
          </SheetContent>
        </Sheet>
      ) : null}

      <div className="relative flex min-w-0 flex-1 flex-col">
        {railTargetId ? (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border/60 pb-2">
            {expertIdentity ? (
              <div className="flex min-w-0 flex-1 items-center gap-2.5">
                <Avatar className="size-5">
                  {expertIdentity.avatarUrl ? (
                    <AvatarImage src={expertIdentity.avatarUrl} alt="" />
                  ) : null}
                  <AvatarFallback className="bg-brand/10 text-brand">
                    <Bot className="size-3" />
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-sm font-medium text-foreground" title={expertIdentity.name}>
                  {expertIdentity.name}
                </span>
              </div>
            ) : null}
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <Tooltip>
                <TooltipTrigger render={
                  <Button variant="ghost" size="icon-sm" aria-label={t("newChat")} onClick={startNewChat} className="text-muted-foreground hover:bg-brand/10 hover:text-brand" />
                }>
                  <MessageSquarePlus className="size-4" />
                </TooltipTrigger>
                <TooltipContent>{t("newChat")}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger render={
                  <Button variant="ghost" size="icon-sm" aria-label={t("history.manage")} onClick={() => loggedIn ? setManagerOpen(true) : promptLogin()} className="text-muted-foreground hover:bg-brand/10 hover:text-brand" />
                }>
                  <History className="size-4" />
                </TooltipTrigger>
                <TooltipContent>{t("history.title")}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        ) : null}
        {/* 窄屏专用：桌面上左栏一直在，不需要这个按钮 */}
        {!railTargetId ? (
          <div className="pb-3 md:hidden">
            <Button
              variant="outline"
              size="sm"
              aria-label={t("openRail")}
              onClick={() => setRailOpen(true)}
            >
              <PanelLeft className="size-4" />
            </Button>
          </div>
        ) : null}

        {/*
          滚动交给 AI Elements 的 Conversation（内部是 use-stick-to-bottom）。

          「流式追加时贴着底、用户往回翻时不抢滚动、离底时给回到底部按钮」
          这三件事此前是手写的，而它们恰恰是最容易做错的一类——
          尤其是「贴底判定」的阈值与时机。
        */}
        {/* 宿主给了 --chat-gutter 就外扩到宿主边上：滚动条贴着外框，内容用同样的数缩回来 */}
        <Conversation className="-mx-[var(--chat-gutter,0px)]">
          <ConversationContent className="space-y-4 py-4 px-[var(--chat-gutter,1rem)]">
          {conversationLoading || (pageLoading && !data) ? <ContentSkeleton variant="chat" label={ui("loading")} /> : pageError && !data ? <div role="alert" className="py-8 text-center text-sm text-destructive">{pageError}<Button variant="link" onClick={() => void loadPage()}>{t("retryNow")}</Button></div> : messages.length === 0 ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              {/* 访客拉不到模型清单，别把它说成「站点没配模型」 */}
              {!loggedIn
                ? t("guestHint")
                : models.length === 0
                  ? t("noModels")
                  : t("startHint")}
            </p>
          ) : null}

          {(conversationLoading ? [] : messages).map((item, index) => {
            const text = chatMessageText(item);
            const isLast = index === messages.length - 1;
            const isLive = isLast && streaming;

            const allTools =
              toolEvents[item.id] ?? (isLive ? pendingTools : []);

            const members = allTools.filter((tool) => tool.kind === "agent");
            const tools = allTools.filter((tool) => tool.kind !== "agent");
            const usage = item.metadata;

            const ownModel = messageModel[item.id];
            const contextWindow = ownModel
              ? (models.find((m) => m.modelId === ownModel)?.contextWindow ?? 0)
              : (activeModel?.contextWindow ?? 0);

            const files = item.parts.filter((part) => part.type === "file");
            const segments = chatMessageSegments(item);

            const drawing =
              isLive &&
              tools.some((tool) => tool.kind === "image" && tool.status === "start");

            const sources = item.parts.flatMap((part) =>
              part.type === "source-url"
                ? [{ url: part.url, title: part.title ?? "" }]
                : [],
            );
            const reasoningPart = item.parts.find(
              (part) => part.type === "reasoning",
            );

            const streamFailed = isLast && Boolean(error) && !streaming;
            const failure =
              item.role !== "assistant"
                ? ""
                : (item.metadata?.error ??
                  (streamFailed ? readChatErrorMessage(error!.message, t("chatFailed")) : ""));
            const failureOwn =
              !item.metadata?.error && streamFailed && isOwnChatError(error!.message);
            const hasReasoning = Boolean(reasoningPart?.text);
            const reasoningStreaming =
              isLive && reasoningPart?.state === "streaming";

            const process =
              item.role !== "assistant"
                ? "none"
                : hasReasoning && tools.length > 0
                  ? "timeline"
                  : hasReasoning
                    ? "reasoning"
                    : tools.length > 0
                      ? "tools"
                      : "none";
            const steps =
              process === "timeline"
                ? buildTurnSteps({
                    hasReasoning,
                    reasoningStreaming,
                    tools,
                    fileCount: files.length,
                    hasText: Boolean(text),
                    streaming: isLive,
                  })
                : [];

            const composing =
              isLive &&
              !text &&
              !imageMode &&
              !reasoningStreaming &&
              !tools.some((tool) => tool.status === "start");

            return (
              <Message
                key={item.id}
                from={item.role}
                className={
                  item.role === "user"
                    ? "[@media(pointer:coarse)]:mb-8"
                    : undefined
                }
              >
                {/* 用户带的附件摆在气泡上方，不挤进文字框里 */}
                {item.role === "user" && files.length > 0 ? (
                  <SentAttachments files={files} />
                ) : null}
                {/* 一条消息里的图算一组：跨消息翻页会把上一轮的图混进来 */}
                <MessageContent
                  className={
                    item.role === "user" ? "relative overflow-visible" : undefined
                  }
                >
                  <ImageGallery className="contents">
                  {/* 只有思考：一行「思考中… / 已深度思考」，想的时候展开，想完自动收起。
                      出图那一轮这条通道放的是润色后的提示词，换个说法 */}
                  {process === "reasoning" ? (
                    <Reasoning
                      className="mb-0"
                      isStreaming={reasoningStreaming}
                      defaultOpen={isLive}
                    >
                      <ReasoningTrigger
                        getThinkingMessage={
                          files.length > 0
                            ? () => t("revisedPromptHint")
                            : undefined
                        }
                      />
                      <ReasoningContent>{reasoningPart?.text ?? ""}</ReasoningContent>
                    </Reasoning>
                  ) : null}

                  {/* 只有工具：一行汇总，点开看每一次调用 */}
                  {process === "tools" ? <ToolSummary tools={tools} /> : null}

                  {/* 思考与工具都有：想 → 调工具 → 写答案，每步带状态。
                      流式中默认展开（正要看的就是进度），结束后留着可回看 */}
                  {process === "timeline" ? (
                    <ChainOfThought defaultOpen={isLive}>
                      <ChainOfThoughtHeader>
                        {isLive
                          ? t("turnSteps")
                          : t("thoughtWithTools", { count: tools.length })}
                      </ChainOfThoughtHeader>
                      <ChainOfThoughtContent>
                        {steps.map((step) => {
                          if (step.kind === "reasoning") {
                            return (
                              <ChainOfThoughtStep
                                key={step.key}
                                icon={Brain}

                                label={
                                  files.length > 0
                                    ? t("stepRevisedPrompt")
                                    : t("stepThinking")
                                }
                                status={step.status}
                              >
                                {/*
                                  推理原文收在这一步里面，默认不展开：
                                  它常常比答案还长，摊开会把答案挤到屏幕外
                                */}
                                <Reasoning
                                  className="mb-0"
                                  defaultOpen={false}
                                  isStreaming={step.status === "active"}
                                >
                                  {/*
                                    出图那一轮里这段文字是润色后的提示词，
                                    不是思考过程，所以不能显示成「思考用时 N 秒」
                                  */}
                                  <ReasoningTrigger
                                    getThinkingMessage={
                                      files.length > 0
                                        ? () => t("revisedPromptHint")
                                        : undefined
                                    }
                                  />
                                  <ReasoningContent>
                                    {reasoningPart?.text ?? ""}
                                  </ReasoningContent>
                                </Reasoning>
                              </ChainOfThoughtStep>
                            );
                          }

                          if (step.kind === "file") {
                            return (
                              <ChainOfThoughtStep
                                key={step.key}
                                icon={ImageIcon}
                                label={t("stepImages", {
                                  count: files.length,
                                })}
                                status={step.status}
                              />
                            );
                          }

                          if (step.kind === "tool" && step.tool) {
                            const event = step.tool;

                            return (
                              <ChainOfThoughtStep
                                key={step.key}
                                icon={Wrench}
                                label={chatToolNote(event, (key, values) =>
                                  t(`toolNote.${key}`, values),
                                )}
                                description={
                                  event.costPoints > 0
                                    ? `${event.serverName} · ${t("costPoints", { points: event.costPoints })}`
                                    : event.serverName
                                }
                                status={step.status}
                              >
                                {event.message ? (
                                  <ChatToolCard event={event} />
                                ) : null}
                              </ChainOfThoughtStep>
                            );
                          }

                          return (
                            <ChainOfThoughtStep
                              key={step.key}
                              icon={PenLine}
                              label={
                                step.status === "complete"
                                  ? t("stepAnswered")
                                  : t("stepAnswering")
                              }
                              status={step.status}
                            />
                          );
                        })}
                      </ChainOfThoughtContent>
                    </ChainOfThought>
                  ) : null}
                  {composing ? (
                    <span className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader size={14} />
                      <Shimmer as="span" duration={1.5}>
                        {t("composing")}
                      </Shimmer>
                    </span>
                  ) : null}

                  {/*
                    模型生成的图片。用 AI Elements 的 Image（按宽度自适应的 img），
                    不套附件那种 96px 缩略图。刷新后不在了——图没有落库。
                  */}
                  {members.length > 0 ? <TeamMemberSteps events={members} /> : null}

                  {/* 出图要等几十秒，空着会让人以为卡死。
                      条件里的 role === "assistant" 不能少，否则用户那条消息
                      右边也会挂一块占位 */}
                  {imageMode &&
                  isLive &&
                  item.role === "assistant" &&
                  files.length === 0 ? (
                    <ImageSkeleton label={t("generatingImage")} />
                  ) : null}

                  {/*
                    回复按出场顺序摆：文字、图片、文字……专家分析完出图、再按图接着说，
                    图就落在两段话中间。只有回复走 Markdown；用户那条保持纯文本：
                    他打的 `*` 就是星号，按 Markdown 渲染会把他写的东西改样子
                  */}
                  {item.role === "assistant"
                    ? segments.map((segment, segmentIndex) =>
                        segment.kind === "file" ? (
                          <Image
                            key={`${item.id}-s-${segmentIndex}`}
                            url={segment.url}
                            mediaType={segment.mediaType}
                            alt={t("generatedImage")}

                            className="max-h-[500px] w-auto max-w-[min(100%,500px)]"
                          />
                        ) : (
                          <StreamingMarkdown
                            key={`${item.id}-s-${segmentIndex}`}
                            text={segment.text}
                            streaming={isLive && segmentIndex === segments.length - 1}
                            sources={sources}
                            onCite={(url) => setSourcePanel({ sources, active: url })}
                          />
                        ),
                      )
                    : text
                      ? <UserText text={text} />
                      : null}

                  {/* 专家正在出图：占位落在已写出的内容后面，就是图将要出现的位置 */}
                  {drawing ? <ImageSkeleton label={t("generatingImage")} /> : null}

                  {/* 回复里的网页、SVG、JSX 代码块：写完之后出一张可预览的卡片 */}
                  {item.role === "assistant" && text && !isLive ? (
                    <ChatArtifacts text={text} />
                  ) : null}

                  {failure ? <ChatErrorNotice raw={failure} own={failureOwn} /> : null}

                  {/* 来源：前三个域名排一行，其余收成「+N」，点哪个都在侧栏里看 */}
                  {sources.length > 0 ? (
                    <SourceChips
                      sources={sources}
                      onOpen={(url) => setSourcePanel({ sources, active: url })}
                    />
                  ) : null}

                  {item.metadata?.aborted && !failure ? (
                    <span className="text-xs text-muted-foreground">
                      {t("aborted")}
                    </span>
                  ) : null}
                  </ImageGallery>
                  {/*
                    提问下面的操作：挂在气泡上、浮在它下面，与气泡同宽、图标靠右，
                    不占一行；鼠标移到提问上才出现，键盘聚焦也显示。隐藏时连点击一起关掉，
                    免得挡住下面回答的字。手机没有悬停，常显，并给下面留出位置。
                    不用 group-hover：Tailwind 把它包在 (hover: hover) 里，远程桌面里的
                    浏览器常报「不支持悬停」，鼠标移上去也不会出现
                  */}
                  {item.role === "user" && text ? (
                    <MessageActions className="pointer-events-none absolute top-full right-0 z-10 w-full justify-end gap-0.5 text-muted-foreground/70 opacity-0 transition-opacity focus-within:pointer-events-auto focus-within:opacity-100 [.group:hover_&]:pointer-events-auto [.group:hover_&]:opacity-100 [@media(pointer:coarse)]:pointer-events-auto [@media(pointer:coarse)]:opacity-100">
                      <CopyButton
                        value={text}
                        label={t("copy")}
                        copiedLabel={t("copied")}
                        successText={t("copied")}
                        errorText={t("copyFailed")}
                        render={(props, { copied }) => <MessageAction {...props} tooltip={copied ? t("copied") : t("copy")} />}
                      >
                        {copied => copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                      </CopyButton>
                      <MessageAction
                        tooltip={t("editQuestion")}
                        onClick={() => editQuestion(item)}
                      >
                        <PenLine className="size-4" />
                      </MessageAction>
                    </MessageActions>
                  ) : null}
                </MessageContent>

                {/*
                  回复下面那一条：左边是操作，右边是这轮的用量。
                  结算 metadata 到达就表示本轮回答已完成；新会话的流可能仍在等标题，
                  操作栏不必跟着等。异常断流未结算的，仍等流结束后显示重试操作。
                */}
                {item.role === "assistant" && (text || failure) && (!isLive || usage) ? (
                  <MessageToolbar className={MESSAGE_TOOLBAR_CLASS}>
                    <MessageActions>
                      <CopyButton
                        value={text || chatErrorText(failure, failureOwn)}
                        label={t("copy")}
                        copiedLabel={t("copied")}
                        successText={t("copied")}
                        errorText={t("copyFailed")}
                        render={(props, { copied }) => <MessageAction {...props} tooltip={copied ? t("copied") : t("copy")} />}
                      >
                        {copied => copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                      </CopyButton>
                      {failure ? (
                        <MessageAction
                          tooltip={t("retryNow")}
                          onClick={() => resendQuestion(index, false)}
                        >
                          <RefreshCw className="size-4" />
                        </MessageAction>
                      ) : null}
                    </MessageActions>

                    <TurnStats
                      usage={usage}
                      tools={allTools}
                      contextWindow={contextWindow}
                    />
                  </MessageToolbar>
                ) : null}
              </Message>
            );
          })}

          {/*
            起流之前就被拦下的（积分不足、专家未购买、模型不支持图片…）：没有回复消息，
            也没落库，在这里补一条错误回复，底栏照常给重新提问
          */}
          {error && !streaming && messages.at(-1)?.role === "user" ? (
            <Message from="assistant">
              <MessageContent>
                <ChatErrorNotice
                  raw={readChatErrorMessage(error.message, t("chatFailed"))}
                  own={isOwnChatError(error.message)}
                />
              </MessageContent>
              <MessageToolbar className={MESSAGE_TOOLBAR_CLASS}>
                <MessageActions>
                  <MessageAction
                    tooltip={t("retryNow")}
                    onClick={() => resendQuestion(messages.length, true)}
                  >
                    <RefreshCw className="size-4" />
                  </MessageAction>
                </MessageActions>
                <TurnStats usage={EMPTY_USAGE} tools={[]} contextWindow={0} />
              </MessageToolbar>
            </Message>
          ) : null}

          {/*
            回复气泡还没出现时的占位：服务端要先解析模型、装工具，上游再吐出第一个字，
            这几秒里 useChat 还没建那条 assistant 消息，不补一块的话只有发送键在转。
            生图这一轮在图回来前同样没有消息，占位换成图片骨架
          */}
          {streaming && messages.at(-1)?.role !== "assistant" ? (
            <Message from="assistant">
              <MessageContent>
                {imageMode ? (
                  <ImageSkeleton label={t("generatingImage")} />
                ) : (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader size={14} />
                    <Shimmer as="span" duration={1.5}>
                      {status === "submitted" ? t("connecting") : t("waitingReply")}
                    </Shimmer>
                  </span>
                )}
              </MessageContent>
            </Message>
          ) : null}
          </ConversationContent>

          <ConversationScrollButton />
        </Conversation>

        {prompt ? (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-dashed bg-muted/40 px-3 py-2">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium">{prompt.name}</p>
              {/* 正文在这儿只给一眼确认，不占输入框：它可能有几千字，
                  塞进去用户得翻到最底下才能打字 */}
              <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs text-muted-foreground">
                {prompt.body}
              </p>
            </div>
            <button
              type="button"
              aria-label={t("removePrompt")}
              className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              onClick={() => setPrompt(null)}
            >
              <X className="size-3.5" />
            </button>
          </div>
        ) : null}

        {/*
          输入区整块是 AI Elements 的 PromptInput：文本域自动高度、Enter 发送
          Shift+Enter 换行、输入法组字期间不误发、发送/停止随 status 切换图标，
          这些原先都是自己写的。
        */}
        {queue.length > 0 ? (
          <Queue className="mt-3">
            <QueueSection>
              <QueueSectionTrigger>
                <QueueSectionLabel count={queue.length} label={t("queued")} />
              </QueueSectionTrigger>
              <QueueSectionContent>
                <QueueList>
                  {queue.map((item) => (
                    <QueueItem key={item.id}>
                      <div className="flex items-center gap-2">
                        <QueueItemIndicator />
                        <QueueItemContent>{item.text}</QueueItemContent>
                        <QueueItemActions>
                          <QueueItemAction
                            aria-label={t("removeQueued")}
                            onClick={() =>
                              setQueue((list) =>
                                list.filter((entry) => entry.id !== item.id),
                              )
                            }
                          >
                            <X className="size-3.5" />
                          </QueueItemAction>
                        </QueueItemActions>
                      </div>
                    </QueueItem>
                  ))}
                </QueueList>
              </QueueSectionContent>
            </QueueSection>
          </Queue>
        ) : null}

        {/* 空对话时把专家的预设问题摆成按钮，点一下放进输入框，改完再发；没买的点了去买 */}
        {!membershipBlocked && !conversationLoading && !pageLoading && messages.length === 0 && agent?.presetQuestions?.length ? (
          <Suggestions className="mt-3">
            {agent.presetQuestions.map((question) => (
              <Suggestion
                key={question}
                suggestion={question}
                onClick={!loggedIn ? promptLogin : purchase ? () => setPurchaseOpen(true) : fillInput}
              />
            ))}
          </Suggestions>
        ) : null}

        {/* 付费专家没买：输入区换成购买条，别让人打完字发出去才被拒 */}
        {membershipBlocked ? (
          <div role="status" className="mt-3 flex items-center gap-3 rounded-[10px] border border-border bg-muted/40 px-4 py-3">
            <Lock className="size-4 shrink-0 text-muted-foreground" />
            <p className="min-w-0 flex-1 text-sm text-foreground">{membershipMessage}</p>
            {!loggedIn ? <Button size="sm" onClick={promptLogin}>{t("membershipLogin")}</Button> : null}
          </div>
        ) : purchase && agent ? (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-[10px] border border-border bg-muted/40 px-4 py-3">
            <Lock className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground">{t("purchase.lockTitle")}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t("purchase.lockPrice", { points: purchase.price })}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                if (!loggedIn) {
                  window.location.href = loginHref;
                  return;
                }

                setPurchaseOpen(true);
              }}
            >
              {loggedIn ? t("purchase.buy") : t("purchase.loginToBuy")}
            </Button>
            {loggedIn ? (
              <AgentPurchaseDialog
                agentId={agent.id}
                open={purchaseOpen}
                onOpenChange={setPurchaseOpen}
                onPurchased={() => {
                  void loadPage();
                  router.refresh();
                }}
              />
            ) : null}
          </div>
        ) : (
        <PromptInput
          className="mt-3"
          onSubmit={handleSubmit}
          onFilesDrop={files => { void handleFilesDrop(files); }}

          {...(loggedIn
            ? {}
            : {
                onClickCapture: (event: ReactMouseEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                  promptLogin();
                },
              })}
        >
          <ComposerAttachments
            picked={picked}
            onRemovePicked={(id) =>
              setPicked((list) => list.filter((item) => item.attachId !== id))
            }
          />
          <PromptInputBody>
            <PromptInputTextarea
              readOnly={!loggedIn || accessPending}
              onFocus={loggedIn ? undefined : promptLogin}
              placeholder={
                imageMode
                  ? t("imagePlaceholder")
                  : prompt
                    ? t("promptPlaceholder")
                    : t("inputPlaceholder")
              }
            />
          </PromptInputBody>

          <PromptInputFooter>
            {/*
              窄屏上这一排装不下（〔实测 375px〕发送键被挤出右边界），
              所以允许换行并让它自己收缩，发送键不参与收缩
            */}
            <PromptInputTools className="min-w-0 flex-1 flex-wrap">
              {/* 附件：点开是附件中间层 */}
              {attachAllowed ? (
                <PromptInputButton
                  aria-label={t("attach")}
                  disabled={uploading || remainingAttachments === 0}
                  onClick={(event) => {
                    event.preventDefault();

                    if (!loggedIn) {
                      promptLogin();
                      return;
                    }

                    setPickerOpen(true);
                  }}
                >
                  <Paperclip className="size-4" />
                </PromptInputButton>
              ) : null}

              {/* 模型切换。触发器只显示模型名，下拉里按平台分组 */}
              <PromptInputSelect
                value={modelId}
                items={modelOptions}

                onValueChange={(value) => pickModel(String(value))}
                disabled={streaming || uploading}
              >
                <PromptInputSelectTrigger className="max-w-40 sm:max-w-56">
                  <PromptInputSelectValue placeholder={t("selectModel")} />
                </PromptInputSelectTrigger>
                <PromptInputSelectContent className="w-auto min-w-56">
                  {modelGroups.map((group) => (
                    <SelectGroup key={group.key}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.items.map((item) => (
                        <PromptInputSelectItem key={item.id} value={item.id}>
                          {/* 生图模型要一眼认出来：选中它之后输入的是画面描述而不是问题。
                              图标位对所有行都留着，模型名才对得齐 */}
                          {hasImageModel ? (
                            <span className="flex size-3.5 shrink-0 items-center justify-center self-center">
                              {item.kind === "image_gen" ? (
                                <ImageIcon className="size-3.5 text-muted-foreground" />
                              ) : null}
                            </span>
                          ) : null}
                          {item.displayName}
                        </PromptInputSelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </PromptInputSelectContent>
              </PromptInputSelect>
              {/* 思考与联网只在当前模型勾了对应能力时出现 */}
              {thinkingAllowed ? (
                <SwitchButton
                  active={thinking}
                  onToggle={() => setThinking((on) => !on)}
                >
                  <Brain className="size-4" />
                  {t("thinking")}
                </SwitchButton>
              ) : null}
              {webSearchAllowed ? (
                <SwitchButton
                  active={webSearch}
                  onToggle={() => setWebSearch((on) => !on)}
                >
                  <Globe className="size-4" />
                  {t("webSearch")}
                </SwitchButton>
              ) : null}

              {showPromptPicker ? (
              <AiPromptPicker
                disabled={streaming}
                onPick={(picked) => {
                  setPrompt(picked);

                  const scaffold = buildPromptScaffold(picked.variables);

                  if (!scaffold) return;

                  const current = textInput.value;
                  textInput.setInput(
                    current.trim()
                      ? `${current.trimEnd()}\n\n${scaffold}`
                      : scaffold,
                  );
                }}
              />
              ) : null}

            </PromptInputTools>

            {/* 余额收在发送键旁边，浅灰小字、与发送键隔开一点；访客没有积分账户，不显示 */}
            {loggedIn ? (
              <span className="mr-2 shrink-0 text-xs text-muted-foreground/70">
                {t("pointsLeft", { points: data?.points ?? 0 })}
              </span>
            ) : null}

            {/*
              流式中这个按钮就是停止键，图标与行为都由 status 决定。
              空着的时候是灰的、点不动（回车也不发），有字或有附件才亮起来
            */}
            <PromptInputSubmit
              className="shrink-0 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100"
              status={uploading ? "submitted" : status}
              disabled={
                conversationLoading || accessPending || (pageLoading && !data) || uploading ||
                (!streaming &&
                  !textInput.value.trim() &&
                  picked.length === 0 &&
                  attachments.files.length === 0)
              }
              onClick={streaming ? () => stop() : undefined}
              type={streaming ? "button" : "submit"}
            />
          </PromptInputFooter>
        </PromptInput>
        )}

        <SourcePanel
          panel={sourcePanel}
          onClose={() => setSourcePanel(null)}
        />

        {loggedIn ? (
          <AiConversationManager
            key={historyAgentId ?? "all"}
            agentId={historyAgentId}
            open={managerOpen}
            onOpenChange={setManagerOpen}
            onOpenConversation={(id) => {
              setManagerOpen(false);
              switchConversation(id);
            }}
            onAction={setConversationAction}
          />
        ) : null}

        {loggedIn ? (
          <ConversationActionDialogs
            action={conversationAction}
            onClose={() => setConversationAction(null)}
            onChanged={(deletedIds) => {
              void loadPage();

              if (conversationId && deletedIds.includes(conversationId)) {
                startNewChat();
              }
            }}
          />
        ) : null}

        {/* 附件中间层：库里挑，或者当场传 */}
        {attachAllowed ? (
          <AttachPickerModal
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            kind="file"
            module="ai"
            allowedKinds={attachmentKinds}
            multiple
            maxCount={remainingAttachments === undefined ? Infinity : Math.max(1, remainingAttachments)}
            onSelect={(record) => addPicked([record])}
            onSelectMany={addPicked}
          />
        ) : null}
      </div>

      {/* 右栏只在工作台出现；会员中心那份不传，版式退回两栏 */}
      {contextPane ? (
        <aside className="hidden w-64 shrink-0 flex-col gap-3 overflow-y-auto border-l pl-4 lg:flex">
          {contextPane}
        </aside>
      ) : null}
    </div>
  );
}

function UserText({ text }: { text: string }) {
  const t = useTranslations("member.aiChat");
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);

  const measure = useCallback((node: HTMLSpanElement | null) => {
    if (node) setClamped(node.scrollHeight > node.clientHeight + 1);
  }, []);

  return (
    <>
      <span
        ref={measure}
        className={cn(
          "whitespace-pre-wrap break-words",
          !expanded && "line-clamp-12",
        )}
      >
        {text}
      </span>
      {clamped ? (
        <button
          type="button"
          className="self-start text-xs text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? t("collapse") : t("expandAll")}
        </button>
      ) : null}
    </>
  );
}

function isImageFile(file: { mediaType: string }) {
  return file.mediaType.startsWith("image/");
}

function ComposerAttachments({
  picked,
  onRemovePicked,
}: {
  picked: SentAttachment[];
  onRemovePicked: (id: string) => void;
}) {
  const dropped = usePromptInputAttachments();
  const items = [
    ...picked.map((file) => ({
      data: {
        id: file.attachId,
        type: "file" as const,
        url: file.url,
        mediaType: file.mediaType,
        filename: file.filename,
      },
      remove: () => onRemovePicked(file.attachId),
    })),
    ...dropped.files.map((file) => ({
      data: file,
      remove: () => dropped.remove(file.id),
    })),
  ];

  if (items.length === 0) return null;

  const images = items.filter((item) => isImageFile(item.data));
  const others = items.filter((item) => !isImageFile(item.data));

  return (
    <PromptInputHeader className="gap-2 pt-2">
      {images.length > 0 ? (
        <Attachments variant="grid" className="ml-0">
          {images.map((item) => (
            <Attachment
              key={item.data.id}
              data={item.data}
              onRemove={item.remove}
              className="size-14"
            >
              <AttachmentPreview />
              <AttachmentRemove className="top-1 right-1 size-5" />
            </Attachment>
          ))}
        </Attachments>
      ) : null}
      {others.length > 0 ? (
        <Attachments variant="inline">
          {others.map((item) => (
            <Attachment key={item.data.id} data={item.data} onRemove={item.remove}>
              <AttachmentPreview />
              <AttachmentInfo className="max-w-40" />
              <AttachmentRemove />
            </Attachment>
          ))}
        </Attachments>
      ) : null}
    </PromptInputHeader>
  );
}

function ToolSummary({ tools }: { tools: ChatToolEvent[] }) {
  const t = useTranslations("member.aiChat");
  const running = tools.find((tool) => tool.status === "start");
  const allSearch = tools.every((tool) => toolKind(tool) === "web_search");
  const Icon = allSearch ? Globe : Wrench;

  return (
    <Collapsible className="not-prose">
      <CollapsibleTrigger className="group flex w-full items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
        <Icon className="size-4" />
        {running ? (
          <Shimmer as="span" duration={1.5}>
            {allSearch
              ? t("searching")
              : t("callingTool", { label: running.label })}
          </Shimmer>
        ) : (
          <span>
            {allSearch
              ? t("searchedTimes", { count: tools.length })
              : t("toolsUsed", { count: tools.length })}
          </span>
        )}
        <ChevronDown className="size-4 transition-transform group-data-[panel-open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 space-y-2">
        {tools.map((tool) => (
          <ChatToolCard key={tool.label} event={tool} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

const EMPTY_USAGE: ChatUIMessage["metadata"] = {
  inputTokens: 0,
  cachedTokens: 0,
  cacheWriteTokens: 0,
  outputTokens: 0,
  costPoints: 0,
  tokenCostPoints: 0,
  mcpCalls: 0,
  mcpCostPoints: 0,
  skillCalls: 0,
  skillCostPoints: 0,
  aborted: true,
};

function ChatErrorNotice({ raw, own }: { raw: string; own: boolean }) {
  const t = useTranslations("member.aiChat");
  const friendly = useChatErrorText()(raw, own);

  return (
    <div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5 text-sm">
      <CircleAlert className="mt-0.5 size-4 shrink-0 text-destructive" />
      <div className="min-w-0 flex-1 space-y-1">
        <p>{friendly}</p>
        {friendly !== raw.trim() ? (
          <Collapsible>
            <CollapsibleTrigger className="group flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
              {t("errorDetail")}
              <ChevronDown className="size-3 transition-transform group-data-[panel-open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-1 font-mono text-xs break-all whitespace-pre-wrap text-muted-foreground">
                {raw}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </div>
    </div>
  );
}

function hostnameOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

const SOURCE_CHIP_CLASS =
  "inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground";

function SourceChips({
  sources,
  onOpen,
}: {
  sources: CitedSource[];
  onOpen: (url?: string) => void;
}) {
  const t = useTranslations("member.aiChat");

  return (
    <div className="not-prose flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{t("sources")}</span>
      {sources.slice(0, 3).map((source) => (
        <button
          key={source.url}
          type="button"
          className={SOURCE_CHIP_CLASS}
          onClick={() => onOpen(source.url)}
        >
          <Globe className="size-3" />
          {hostnameOf(source.url)}
        </button>
      ))}
      {sources.length > 3 ? (
        <button type="button" className={SOURCE_CHIP_CLASS} onClick={() => onOpen()}>
          +{sources.length - 3}
        </button>
      ) : null}
    </div>
  );
}

function SourcePanel({
  panel,
  onClose,
}: {
  panel: { sources: CitedSource[]; active?: string } | null;
  onClose: () => void;
}) {
  const t = useTranslations("member.aiChat");
  const scrollIntoView = useCallback((node: HTMLLIElement | null) => {
    node?.scrollIntoView({ block: "nearest" });
  }, []);

  return (
    <Sheet open={Boolean(panel)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-sm">
        <SheetHeader className="border-b px-4 py-3">
          <SheetTitle>
            {t("sourcesTitle", { count: panel?.sources.length ?? 0 })}
          </SheetTitle>
        </SheetHeader>
        <ol className="flex-1 space-y-2 overflow-y-auto p-3">
          {(panel?.sources ?? []).map((source, index) => {
            const active = source.url === panel?.active;

            return (
              <li
                key={source.url}
                ref={active ? scrollIntoView : undefined}
                className={cn(
                  "rounded-lg border p-3 transition-colors",
                  active && "border-brand/40 bg-brand/5",
                )}
              >
                <p className="text-xs text-muted-foreground">
                  {index + 1} · {hostnameOf(source.url)}
                </p>
                <p className="mt-0.5 line-clamp-2 text-sm font-medium">
                  {source.title || hostnameOf(source.url)}
                </p>
                <p className="mt-1 truncate text-xs text-muted-foreground">
                  {source.url}
                </p>
                <Source
                  href={source.url}
                  rel="nofollow noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                >
                  {t("openSource")}
                  <ExternalLink className="size-3" />
                </Source>
              </li>
            );
          })}
        </ol>
      </SheetContent>
    </Sheet>
  );
}

const MESSAGE_TOOLBAR_CLASS = "mt-2 justify-start gap-1 text-muted-foreground/70";

const STAT_BUTTON_CLASS =
  "inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-xs transition-colors hover:bg-muted hover:text-foreground";

function toolKind(
  tool: ChatToolEvent,
): "web_search" | "skill" | "mcp" | "agent" | "image" {
  return tool.kind ?? (tool.serverName ? "mcp" : "web_search");
}

const TOOL_ICONS = {
  web_search: Globe,
  skill: Sparkles,
  mcp: Wrench,
  agent: UsersRound,
  image: ImageIcon,
} as const;

function TeamMemberSteps({ events }: { events: ChatToolEvent[] }) {
  const t = useTranslations("member.aiChat.team");

  return (
    <div className="not-prose space-y-2">
      {events.map((event) => {
        const running = event.status === "start";
        const failed = event.status === "error";

        return (
          <Collapsible
            key={event.callId ?? event.label}
            className="rounded-[10px] border border-border/70 bg-muted/30 px-3 py-2"
          >
            <CollapsibleTrigger
              disabled={!event.output}
              className="group flex w-full items-center gap-2 text-left text-sm disabled:cursor-default"
            >
              {event.avatarUrl ? (

                // eslint-disable-next-line @next/next/no-img-element
                <img src={event.avatarUrl} alt="" className="size-5 shrink-0 rounded-full object-cover" />
              ) : (
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] text-muted-foreground">
                  {event.label.slice(0, 1)}
                </span>
              )}
              <span className="shrink-0 font-medium text-foreground">{event.label}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                {running ? (
                  <Shimmer as="span" duration={1.5}>
                    {t("working")}
                  </Shimmer>
                ) : failed ? (
                  <span className="text-destructive">{t("failed")}</span>
                ) : (
                  t("done")
                )}
                {event.costPoints > 0 ? ` · ${t("points", { points: event.costPoints })}` : ""}
              </span>
              {event.output ? (
                <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[panel-open]:rotate-180" />
              ) : null}
            </CollapsibleTrigger>
            {event.message ? (
              <p
                className={cn(
                  "mt-1 line-clamp-2 text-xs",
                  failed ? "text-destructive" : "text-muted-foreground",
                )}
              >
                {failed ? event.message : t("task", { task: event.message })}
              </p>
            ) : null}
            {event.output ? (
              <CollapsibleContent className="mt-2 border-t border-border/60 pt-2 text-sm">
                <StreamingMarkdown text={event.output} />
              </CollapsibleContent>
            ) : null}
          </Collapsible>
        );
      })}
    </div>
  );
}

function TurnStats({
  usage,
  tools,
  contextWindow,
}: {
  usage: ChatUIMessage["metadata"];
  tools: ChatToolEvent[];
  contextWindow: number;
}) {
  const t = useTranslations("member.aiChat");
  const locale = useLocale();

  if (!usage) return null;

  const total = usage.inputTokens + usage.outputTokens;

  const usedTools = tools.reduce<
    {
      key: string;
      kind: ReturnType<typeof toolKind>;
      platform: string;
      name: string;
      calls: number;
      cost: number;
    }[]
  >((list, tool) => {
    const kind = toolKind(tool);
    const name = kind === "web_search" ? t("toolKind.web_search") : tool.label;
    const platform = tool.serverName || t(`toolKind.${kind}`);
    const key = JSON.stringify([kind, platform, name]);
    const found = list.find((item) => item.key === key);

    if (found) {
      found.calls += 1;
      found.cost += tool.costPoints;
    } else {
      list.push({ key, kind, platform, name, calls: 1, cost: tool.costPoints });
    }

    return list;
  }, []);
  const tokens = (count: number) =>
    t("tokenCount", { count: count.toLocaleString("zh-CN") });
  const row = (label: string, value: ReactNode) => (
    <div className="flex items-center gap-2 text-xs">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <Separator aria-hidden="true" className="min-w-3 flex-1 border-t border-dotted border-muted-foreground/30 bg-transparent data-horizontal:w-auto" />
      <span className="whitespace-nowrap tabular-nums">{value}</span>
    </div>
  );

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {usage.durationMs ? (
        <span className="whitespace-nowrap px-1.5 text-xs tabular-nums">
          {t("duration")} {t("seconds", { seconds: (usage.durationMs / 1000).toFixed(1) })}
        </span>
      ) : null}
      {tools.length > 0 ? (
        <Popover>
          <PopoverTrigger
            render={<button type="button" className={STAT_BUTTON_CLASS} />}
          >
            <Wrench className="size-3.5" />
            {t("toolsCount", { count: tools.length })}
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-auto min-w-40 max-w-64 gap-1.5 p-3">
            {usedTools.map((tool) => {
              const Icon = TOOL_ICONS[tool.kind];

              return (
                <div key={tool.key} className="flex items-center gap-2 text-xs">
                  <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                  <Tooltip>
                    <TooltipTrigger render={
                      <button type="button" className="min-w-0 flex-1 truncate text-left" />
                    }>
                      {tool.name}
                      {tool.calls > 1 ? ` ×${tool.calls}` : ""}
                    </TooltipTrigger>
                    <TooltipContent align="start">
                      <div className="space-y-0.5 [overflow-wrap:anywhere]">
                        <div className="text-tooltip-foreground/65">{tool.platform}</div>
                        <div>{tool.name}</div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  {tool.cost > 0 ? (
                    <span className="shrink-0 text-muted-foreground">
                      {t("pointsCost", { points: tool.cost })}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </PopoverContent>
        </Popover>
      ) : null}

      {usage ? (
        <Context
          maxTokens={contextWindow}
          usedTokens={total}
          usage={{
            inputTokens: usage.inputTokens,
            outputTokens: usage.outputTokens,
            totalTokens: total,
            inputTokenDetails: {
              noCacheTokens: Math.max(0, usage.inputTokens - usage.cachedTokens - (usage.cacheWriteTokens ?? 0)),
              cacheReadTokens: usage.cachedTokens,
              cacheWriteTokens: usage.cacheWriteTokens ?? 0,
            },
            outputTokenDetails: {
              textTokens: undefined,
              reasoningTokens: undefined,
            },
          }}
        >
          <ContextTrigger>
            <button type="button" className={STAT_BUTTON_CLASS}>
              <Coins className="size-3.5" />
              {t("pointsCost", { points: usage.costPoints })}
            </button>
          </ContextTrigger>
          <ContextContent side="top" align="start" className="min-w-[230px] max-w-[calc(100vw-2rem)]">
            <ContextContentBody className="space-y-1.5">
              {row(t("inputTokens"), tokens(usage.inputTokens))}
              {row(t("cacheWriteTokens"), tokens(usage.cacheWriteTokens ?? 0))}
              {row(t("cachedTokens"), tokens(usage.cachedTokens))}
              {row(t("outputTokens"), tokens(usage.outputTokens))}
              {row(t("modelPoints"), usage.tokenCostPoints)}
              {row(t("toolPoints"), usage.costPoints - usage.tokenCostPoints)}
            </ContextContentBody>
            {/* 消耗积分含按次收费的工具（MCP / 技能），每个工具花了多少在「工具」弹层里 */}
            <ContextContentFooter>
              <span className="text-muted-foreground">{t("pointsDeducted")}</span>
              <span className="whitespace-nowrap tabular-nums">{usage.costPoints}</span>
            </ContextContentFooter>
          </ContextContent>
        </Context>
      ) : null}

      {usage.createdAt ? (
        <span className="whitespace-nowrap px-1.5 text-xs tabular-nums">
          {formatDateTime(usage.createdAt, locale)}
        </span>
      ) : null}
    </div>
  );
}

function SwitchButton({
  active,
  onToggle,
  children,
}: {
  active: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <PromptInputButton
      aria-pressed={active}
      className={cn(
        "gap-1",
        active && "bg-brand/10 text-brand hover:bg-brand/15 hover:text-brand",
      )}
      onClick={(event) => {
        event.preventDefault();
        onToggle();
      }}
    >
      {children}
    </PromptInputButton>
  );
}
